import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import {
  createProjectArea,
  deleteProjectArea,
  getAreasBaseline,
  getProjectAreas
} from '../../api/inspections';
import type { AreaBaseline, ProjectArea } from '../../api/inspections';
import {
  mainColors,
  secondaryColors,
  successColors
} from '../../theme/themeConfig';
import useNotificationsStore from '../../store/zustand/notifications';

const CONFIDENCE_TARGET = 15;

const AREA_TYPES = [
  'restroom',
  'lobby',
  'office',
  'breakroom',
  'conference',
  'corridor',
  'stairwell',
  'exterior',
  'other'
];

const StyledHeaderCell = styled(TableCell)(() => ({
  background: '#F4F7FB',
  borderBottom: `1px solid ${secondaryColors.SECONDARY_6}`,
  color: secondaryColors.SECONDARY,
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '.6px',
  padding: '8px 14px',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap'
}));

const StyledRow = styled(TableRow)(() => ({
  '&:hover': { background: mainColors.MAIN_LIGHT },
  '&:last-child td': { borderBottom: 0 }
}));

function confidenceColor(level: number): string {
  if (level >= 70) return successColors.SUCCESS;
  if (level >= 30) return '#F59E0B';
  return '#CBD5E1';
}

function confidenceLabel(level: number, photoCount: number): string {
  if (level >= 70) return 'Strong baseline';
  if (level >= 30) return 'Unlocked';
  return `${photoCount}/${CONFIDENCE_TARGET} photos`;
}

function scoreColor(score: number): string {
  if (score >= 4) return successColors.SUCCESS;
  if (score >= 2.5) return '#F59E0B';
  return '#EF4444';
}

interface Props {
  projectId: string;
}

const ProjectAreasPanel: React.FC<Props> = function ProjectAreasPanel({
  projectId
}) {
  const { addNotification } = useNotificationsStore((s) => s);
  const [areas, setAreas] = useState<ProjectArea[]>([]);
  const [baselines, setBaselines] = useState<AreaBaseline[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState('');
  const [saving, setSaving] = useState(false);

  const load = () => {
    Promise.all([
      getProjectAreas(projectId),
      getAreasBaseline(projectId).catch(() => [])
    ])
      .then(([a, b]) => {
        setAreas(a);
        setBaselines(b);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [projectId]);
  useEffect(() => {
    if (open) load();
  }, [open]);

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      await createProjectArea(projectId, {
        areaType: newType || undefined,
        name: newName.trim(),
        orderIndex: areas.length
      });
      setNewName('');
      setNewType('');
      setAdding(false);
      load();
      addNotification('Area added', 'success');
    } catch {
      addNotification('Failed to add area', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteProjectArea(id);
      setAreas((prev) => prev.filter((a) => a.id !== id));
      setBaselines((prev) => prev.filter((b) => b.areaId !== id));
      addNotification('Area removed', 'success');
    } catch {
      addNotification('Failed to remove area', 'error');
    }
  };

  const handleClose = () => {
    setOpen(false);
    setAdding(false);
    setNewName('');
    setNewType('');
  };

  const baselineMap = new Map(baselines.map((b) => [b.areaId, b]));
  const baselinedCount = baselines.filter(
    (b) => b.confidenceLevel >= 30
  ).length;
  const preview =
    areas
      .slice(0, 3)
      .map((a) => a.name)
      .join(', ') + (areas.length > 3 ? ` +${areas.length - 3} more` : '');

  return (
    <>
      <Box sx={{ borderBottom: '1px solid #E2E4EA', p: '14px 16px' }}>
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'space-between'
          }}
        >
          <Typography
            sx={{
              color: '#7D8090',
              fontSize: '10px',
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase'
            }}
          >
            Inspection Areas
          </Typography>
          <IconButton
            onClick={() => setOpen(true)}
            size="small"
            sx={{ color: '#7D8090', p: '2px' }}
          >
            <TuneOutlinedIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>

        {loading && <CircularProgress size={14} sx={{ mt: 0.5 }} />}

        {!loading && areas.length === 0 && (
          <Box
            onClick={() => setOpen(true)}
            sx={{
              '&:hover': { color: mainColors.MAIN },
              alignItems: 'center',
              color: secondaryColors.SECONDARY,
              cursor: 'pointer',
              display: 'inline-flex',
              gap: 0.5,
              mt: 0.5
            }}
          >
            <AddIcon sx={{ fontSize: 13 }} />
            <Typography fontSize={12}>Add inspection areas</Typography>
          </Box>
        )}

        {!loading && areas.length > 0 && (
          <Box
            onClick={() => setOpen(true)}
            sx={{ cursor: 'pointer', mt: 0.5 }}
          >
            <Typography fontSize={12} fontWeight={500}>
              {areas.length} area{areas.length !== 1 ? 's' : ''}
              {baselinedCount > 0 && (
                <Box
                  component="span"
                  sx={{ color: successColors.SUCCESS, ml: 0.5 }}
                >
                  ({baselinedCount} baselined)
                </Box>
              )}
            </Typography>
            <Typography
              color={secondaryColors.SECONDARY}
              fontSize={11}
              mt={0.2}
            >
              {preview}
            </Typography>
          </Box>
        )}
      </Box>

      <Dialog fullWidth maxWidth="md" onClose={handleClose} open={open}>
        <DialogTitle>
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'space-between'
            }}
          >
            <Box>
              <Typography fontSize={18} fontWeight={700}>
                Inspection Areas
              </Typography>
              <Typography color={secondaryColors.SECONDARY} fontSize={13}>
                {areas.length} area{areas.length !== 1 ? 's' : ''} -{' '}
                {baselinedCount} baselined - {areas.length - baselinedCount}{' '}
                need photos
              </Typography>
            </Box>
            <Button
              onClick={() => setAdding((v) => !v)}
              size="small"
              startIcon={<AddIcon />}
              variant="outlined"
            >
              Add Area
            </Button>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pb: 3 }}>
          {adding && (
            <Box
              sx={{
                background: '#F8FAFC',
                border: `1px solid ${secondaryColors.SECONDARY_6}`,
                borderRadius: '10px',
                mb: 2,
                p: '14px'
              }}
            >
              <Typography
                color={secondaryColors.SECONDARY}
                fontSize={12}
                fontWeight={600}
                mb={1}
              >
                New Area
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <TextField
                  autoFocus
                  fullWidth
                  label="Area name"
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleAdd();
                  }}
                  size="small"
                  value={newName}
                />
                <Box>
                  <Typography
                    color={secondaryColors.SECONDARY}
                    fontSize={11}
                    mb={0.5}
                  >
                    Type (optional)
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {AREA_TYPES.map((t) => (
                      <Chip
                        clickable
                        key={t}
                        label={t}
                        onClick={() => setNewType(newType === t ? '' : t)}
                        size="small"
                        sx={{
                          background: newType === t ? '#E1F5EE' : undefined,
                          border:
                            newType === t ? '1px solid #0F6E56' : undefined,
                          color: newType === t ? '#0F6E56' : undefined,
                          fontSize: 10,
                          textTransform: 'capitalize'
                        }}
                      />
                    ))}
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    onClick={() => {
                      setAdding(false);
                      setNewName('');
                      setNewType('');
                    }}
                    size="small"
                    variant="outlined"
                  >
                    Cancel
                  </Button>
                  <Button
                    disabled={!newName.trim() || saving}
                    onClick={handleAdd}
                    size="small"
                    variant="contained"
                  >
                    {saving ? (
                      <CircularProgress color="inherit" size={14} />
                    ) : (
                      'Add'
                    )}
                  </Button>
                </Box>
              </Box>
            </Box>
          )}

          {areas.length === 0 ? (
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                py: 6
              }}
            >
              <TuneOutlinedIcon sx={{ color: '#CBD5E1', fontSize: 40 }} />
              <Typography color={secondaryColors.SECONDARY} fontSize={14}>
                No areas defined yet
              </Typography>
              <Button
                onClick={() => setAdding(true)}
                size="small"
                variant="outlined"
              >
                Add your first area
              </Button>
            </Box>
          ) : (
            <Box
              sx={{
                border: `1px solid ${secondaryColors.SECONDARY_6}`,
                borderRadius: '10px',
                overflow: 'hidden'
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <StyledHeaderCell>Area</StyledHeaderCell>
                    <StyledHeaderCell>Type</StyledHeaderCell>
                    <StyledHeaderCell>Baseline</StyledHeaderCell>
                    <StyledHeaderCell>Avg Score</StyledHeaderCell>
                    <StyledHeaderCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {areas.map((area) => {
                    const b = baselineMap.get(area.id);
                    const photos = b?.photoCount ?? 0;
                    const confidence = b?.confidenceLevel ?? 0;
                    const avgScore = b?.avgScore ?? null;
                    const pct = Math.min(
                      100,
                      (photos / CONFIDENCE_TARGET) * 100
                    );
                    const color = confidenceColor(confidence);

                    return (
                      <StyledRow key={area.id}>
                        <TableCell sx={{ fontWeight: 600, py: 1.5 }}>
                          {area.name}
                        </TableCell>
                        <TableCell sx={{ py: 1.5 }}>
                          {area.areaType ? (
                            <Chip
                              label={area.areaType}
                              size="small"
                              sx={{
                                fontSize: 10,
                                height: 18,
                                textTransform: 'capitalize'
                              }}
                            />
                          ) : (
                            <Typography
                              color={secondaryColors.SECONDARY}
                              fontSize={12}
                            >
                              -
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ py: 1.5 }}>
                          <Box sx={{ maxWidth: 200 }}>
                            <Box
                              sx={{
                                alignItems: 'center',
                                display: 'flex',
                                gap: 0.8,
                                mb: 0.5
                              }}
                            >
                              {confidence >= 30 ? (
                                <CheckCircleOutlineIcon
                                  sx={{ color, flexShrink: 0, fontSize: 14 }}
                                />
                              ) : (
                                <Box
                                  sx={{
                                    background: color,
                                    borderRadius: '50%',
                                    flexShrink: 0,
                                    height: 8,
                                    width: 8
                                  }}
                                />
                              )}
                              <Typography fontSize={12} sx={{ color }}>
                                {confidenceLabel(confidence, photos)}
                              </Typography>
                            </Box>
                            <Tooltip
                              title={`${photos} of ${CONFIDENCE_TARGET} baseline photos captured`}
                            >
                              <LinearProgress
                                sx={{
                                  '& .MuiLinearProgress-bar': {
                                    backgroundColor: color
                                  },
                                  backgroundColor: '#E2E8F0',
                                  borderRadius: 4,
                                  height: 4
                                }}
                                value={pct}
                                variant="determinate"
                              />
                            </Tooltip>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ py: 1.5 }}>
                          {avgScore !== null ? (
                            <Typography
                              fontSize={13}
                              fontWeight={600}
                              sx={{ color: scoreColor(avgScore) }}
                            >
                              {avgScore.toFixed(1)}
                            </Typography>
                          ) : (
                            <Typography
                              color={secondaryColors.SECONDARY}
                              fontSize={12}
                            >
                              No inspections
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ py: 1.5, textAlign: 'right' }}>
                          <IconButton
                            onClick={() => handleDelete(area.id)}
                            size="small"
                            sx={{ color: '#CBD5E1' }}
                          >
                            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </TableCell>
                      </StyledRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}

          {areas.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
              {[
                {
                  color: '#CBD5E1',
                  label: `< ${CONFIDENCE_TARGET} photos - not yet baselined`
                },
                {
                  color: '#F59E0B',
                  label: 'Unlocked - surprise inspections enabled (30+ photos)'
                },
                {
                  color: successColors.SUCCESS,
                  label:
                    'Strong baseline - highly specific AI prompts (70+ confidence)'
                }
              ].map(({ color, label }) => (
                <Box
                  key={label}
                  sx={{ alignItems: 'center', display: 'flex', gap: 0.5 }}
                >
                  <Box
                    sx={{
                      background: color,
                      borderRadius: '50%',
                      height: 8,
                      width: 8
                    }}
                  />
                  <Typography color={secondaryColors.SECONDARY} fontSize={11}>
                    {label}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ProjectAreasPanel;
